/**
 * Modal Component
 *
 * A dialog overlay component with header, body, and footer sections.
 * Supports accessibility features including focus trapping and keyboard navigation.
 *
 * Validates: Requirements 1.2, 1.3, 1.6
 * - Requirement 1.2: THE Console SHALL use modern UI patterns including cards
 *   with subtle shadows, rounded corners, and smooth transitions.
 * - Requirement 1.3: WHEN a user interacts with interactive elements, THE Console
 *   SHALL provide visual feedback within 100ms.
 * - Requirement 1.6: THE Console SHALL use accessible color contrasts meeting
 *   WCAG 2.1 AA standards for all text elements.
 */

import React, { forwardRef, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { tokens } from '../tokens';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Size of the modal */
  size?: ModalSize;
  /** Title displayed in the header */
  title?: React.ReactNode;
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Whether clicking the overlay closes the modal */
  closeOnOverlayClick?: boolean;
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean;
  /** Content for the modal body */
  children: React.ReactNode;
  /** Content for the modal footer */
  footer?: React.ReactNode;
  /** Additional class name for the modal */
  className?: string;
  /** Additional styles for the modal */
  style?: React.CSSProperties;
}

/**
 * Get size-specific styles
 */
function getSizeStyles(size: ModalSize): React.CSSProperties {
  switch (size) {
    case 'sm':
      return { maxWidth: '400px' };
    case 'lg':
      return { maxWidth: '800px' };
    case 'xl':
      return { maxWidth: '1024px' };
    case 'full':
      return { maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 48px)' };
    case 'md':
    default:
      return { maxWidth: '560px' };
  }
}

/**
 * Close icon component
 */
function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M5 5l10 10M15 5l-10 10" />
    </svg>
  );
}

/**
 * Modal component for displaying dialogs and overlays.
 *
 * Features:
 * - Accessible dialog with proper ARIA attributes
 * - Focus trapping within the modal
 * - Keyboard navigation (Escape to close)
 * - Click outside to close (optional)
 * - Smooth animations for open/close
 * - Multiple size options
 *
 * @example
 * ```tsx
 * // Basic modal
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirm Action"
 *   footer={
 *     <>
 *       <Button variant="ghost" onClick={() => setIsOpen(false)}>
 *         Cancel
 *       </Button>
 *       <Button variant="primary" onClick={handleConfirm}>
 *         Confirm
 *       </Button>
 *     </>
 *   }
 * >
 *   <p>Are you sure you want to proceed?</p>
 * </Modal>
 * ```
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    isOpen,
    onClose,
    size = 'md',
    title,
    showCloseButton = true,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    children,
    footer,
    className,
    style,
  },
  ref
) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Escape key — stable listener; onClose via ref avoids re-subscribing each parent render.
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape]);

  // Focus trap entry/exit — only when opening/closing, not on every parent re-render.
  useEffect(() => {
    if (!isOpen) return;

    previousActiveElement.current = document.activeElement as HTMLElement;
    modalRef.current?.focus();
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
      previousActiveElement.current?.focus();
    };
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const overlayStyles: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    padding: tokens.spacing[4],
    // Animation
    animation: 'modalOverlayFadeIn 150ms ease-out',
  };

  const modalStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '14px',
    border: `1px solid ${tokens.colors.neutral[200]}`,
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.12)',
    outline: 'none',
    maxHeight: 'calc(100vh - 48px)',
    // Animation
    animation: 'modalContentSlideIn 150ms ease-out',
    ...getSizeStyles(size),
    ...style,
  };

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 22px',
    borderBottom: `1px solid ${tokens.colors.neutral[100]}`,
    flexShrink: 0,
  };

  const titleStyles: React.CSSProperties = {
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: '16px',
    fontWeight: 700,
    color: tokens.colors.neutral[900],
    margin: 0,
    lineHeight: tokens.typography.lineHeight.tight,
  };

  const closeButtonStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    padding: 0,
    border: 'none',
    borderRadius: '8px',
    backgroundColor: tokens.colors.neutral[50],
    color: tokens.colors.neutral[500],
    cursor: 'pointer',
    transition: `all ${tokens.transitions.fast}`,
    fontSize: '18px',
  };

  const bodyStyles: React.CSSProperties = {
    flex: 1,
    padding: '22px',
    overflow: 'auto',
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize.sm,
    color: tokens.colors.neutral[700],
    lineHeight: tokens.typography.lineHeight.normal,
  };

  const footerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '16px 22px',
    borderTop: `1px solid ${tokens.colors.neutral[100]}`,
    flexShrink: 0,
  };

  // CSS animations
  const animationStyles = `
    @keyframes modalOverlayFadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes modalContentSlideIn {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(-10px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    .ds-modal-close-button:hover {
      background-color: ${tokens.colors.neutral[100]};
      color: ${tokens.colors.neutral[700]};
    }

    .ds-modal-close-button:focus-visible {
      outline: 2px solid ${tokens.colors.primary[500]};
      outline-offset: 2px;
    }
  `;

  const modalContent = (
    <>
      <style>{animationStyles}</style>
      <div
        style={overlayStyles}
        onClick={handleOverlayClick}
        aria-hidden="true"
      >
        <div
          ref={(node) => {
            // Handle both refs
            (modalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          tabIndex={-1}
          style={modalStyles}
          className={className}
          onClick={(e) => e.stopPropagation()}
        >
          {(title || showCloseButton) && (
            <div style={headerStyles}>
              {title && (
                <h2 id="modal-title" style={titleStyles}>
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  type="button"
                  className="ds-modal-close-button"
                  style={closeButtonStyles}
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          )}
          <div style={bodyStyles}>{children}</div>
          {footer && <div style={footerStyles}>{footer}</div>}
        </div>
      </div>
    </>
  );

  // Render in portal
  return createPortal(modalContent, document.body);
});

export default Modal;
